export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'outline'
  | 'ghost'
  | 'danger'
  | 'icon-nav'

export type PrimaryWeight = 'semibold' | 'medium'
export type OutlineTone = 'auth' | 'toolbar'

const basePlate =
  'inline-flex h-11 items-center justify-center rounded-md px-4 text-body focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50'

const primaryBase =
  `${basePlate} border border-transparent bg-brand-primary text-text-inverse hover:bg-brand-primary-hover focus:ring-interactive-focus-ring`

const outlineAuth =
  `${basePlate} border border-border-strong bg-surface-default font-medium text-text-secondary shadow-sm hover:bg-surface-subtle focus:ring-interactive-focus-ring`

const outlineToolbar =
  `${basePlate} border border-border-default bg-surface-default font-medium text-text-primary hover:bg-surface-muted focus:ring-interactive-focus-ring`

const secondaryBase =
  `${basePlate} border border-border-strong bg-surface-default font-medium text-text-secondary hover:bg-surface-muted focus:ring-border-strong`

const ghostBase =
  `${basePlate} font-medium text-text-secondary hover:bg-surface-muted hover:text-text-primary`

const dangerBase =
  `${basePlate} bg-danger font-medium text-text-inverse hover:bg-danger-hover focus:ring-danger`

const iconNavBase =
  'inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-interactive-focus-ring'

const iconNavInactive = `${iconNavBase} bg-surface-muted text-text-secondary`
const iconNavActive = `${iconNavBase} bg-brand-primary-subtle text-brand-primary`

export interface ButtonClassOptions {
  variant: ButtonVariant
  fullWidth?: boolean
  primaryWeight?: PrimaryWeight
  outlineTone?: OutlineTone
  active?: boolean
  className?: string
}

export function getButtonClassName({
  variant,
  fullWidth = false,
  primaryWeight = 'semibold',
  outlineTone = 'auth',
  active = false,
  className = '',
}: ButtonClassOptions): string {
  const width = fullWidth ? 'w-full' : ''
  let variantClass = primaryBase

  switch (variant) {
    case 'primary':
      variantClass = `${primaryBase} ${primaryWeight === 'semibold' ? 'font-semibold' : 'font-medium'}`
      break
    case 'outline':
      variantClass = outlineTone === 'toolbar' ? outlineToolbar : outlineAuth
      break
    case 'secondary':
      variantClass = secondaryBase
      break
    case 'ghost':
      variantClass = ghostBase
      break
    case 'danger':
      variantClass = dangerBase
      break
    case 'icon-nav':
      variantClass = active ? iconNavActive : iconNavInactive
      break
    default:
      break
  }

  return [variantClass, width, className].filter(Boolean).join(' ')
}

/** Collapsed sidebar nav link plate — same 44×44 target as icon-nav */
export const NAV_ICON_LINK_CLASS =
  'flex h-11 w-11 shrink-0 items-center justify-center rounded-md transition-colors duration-fast'

export const NAV_ICON_LINK_INACTIVE = `${NAV_ICON_LINK_CLASS} bg-transparent hover:bg-surface-muted`
export const NAV_ICON_LINK_ACTIVE = `${NAV_ICON_LINK_CLASS} bg-brand-primary-subtle`
